import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_EPOCUS_REVIEW_COLLECTION_NAME = "epocus-review";

export const useRxCollectionEpocusReview =
  (): RxCollection<EpocusReview> | null =>
    useRxCollection<EpocusReview>(RXDB_EPOCUS_REVIEW_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<EpocusReview>>;
export type EpocusReviewQueryConstructor = UseRxData["1"];

const EPOCUS_REVIEW_SCHEMA_LITERAL = {
  title: "epocus review schema",
  version: 3,
  description: "Represents a QA review of a POCUS exam",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    examId: {
      type: "string",
    },
    reviewerId: {
      type: "string",
    },
    agreement: {
      type: "string",
      enum: ["agree", "minor_discrepancy", "major_discrepancy", "disagree"],
    },
    imageQualityScore: {
      type: ["number", "null"],
      minimum: 1,
      maximum: 5,
      multipleOf: 1,
    },
    qualityScore: {
      type: "number",
      minimum: 1,
      maximum: 5,
      multipleOf: 0.5,
    },
    feedbackTags: {
      type: "array",
      items: {
        type: "string",
      },
    },
    comment: {
      type: ["string", "null"],
    },
    signedAt: {
      type: ["string", "null"],
      format: "date-time",
    },
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "examId",
    "reviewerId",
    "agreement",
    "qualityScore",
    "feedbackTags",
    "meta",
  ],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_REVIEW_SCHEMA_LITERAL);

export type EpocusReview = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const EPOCUS_REVIEW_SCHEMA: RxJsonSchema<EpocusReview> =
  EPOCUS_REVIEW_SCHEMA_LITERAL;
